import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Monitor, Wifi, WifiOff, AlertOctagon, CheckCircle2, Wrench, HardDrive, Cpu, MemoryStick, Clock, DatabaseBackup, OctagonX } from "lucide-react";
import { useFetch, ago, fmtTime } from "@/lib/api";
import { PageHeader, Stat, Loading, ErrorBox, Empty } from "@/components/common";
import { useAuth, can } from "@/context/AuthContext";
import { RepairFeed, RealDeviceCard } from "@/components/RepairFeed";

export default function Dashboard() {
  const { orgId, role } = useAuth();
  const { data: d, loading, error, reload } = useFetch("/dashboard", [orgId]);
  useEffect(() => {
    const t = setInterval(reload, 10000);
    return () => clearInterval(t);
  }, [reload]);
  if (loading && !d) return <Loading />;
  if (error && !d) return <ErrorBox error={error} />;
  const sub = d.subscription;
  return (
    <div className="fade-up">
      <PageHeader title="Fleet overview" crumbs={["Portal", "Dashboard"]}>
        {can.manage(role) && <Link to="/portal/enroll" className="px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700" data-testid="dashboard-enroll-btn">Enroll device</Link>}
      </PageHeader>
      {d.emergency_stop && (
        <div className="mb-6 bg-red-600 text-white p-4 flex items-center gap-3" data-testid="emergency-stop-banner"><OctagonX className="w-5 h-5" />Emergency stop is ACTIVE — all corrective automation is disabled. Read-only diagnostics continue.</div>
      )}
      {d.devices.test_harness > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3" data-testid="test-harness-notice">{d.devices.test_harness} device(s) are reporting SYNTHETIC telemetry from the CodeRed test harness, not a real Windows agent.</div>
      )}
      {d.devices.total === 0 ? (
        <Empty title="No devices enrolled yet" text="Generate an enrollment token and install the CodeRed agent on a Windows computer or server. Dashboards fill with real telemetry once it checks in." testid="dashboard-empty">
          {can.manage(role) && <Link to="/portal/enroll" className="px-4 py-2 bg-gray-900 text-white text-sm">Start enrollment wizard</Link>}
        </Empty>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Registered devices" value={d.devices.total} icon={Monitor} testid="stat-devices-total" sub={`${d.devices.pending} awaiting first check-in`} />
            <Stat label="Online" value={d.devices.online} icon={Wifi} tone="good" testid="stat-devices-online" />
            <Stat label="Offline / stale" value={d.devices.offline} icon={WifiOff} tone={d.devices.offline ? "bad" : null} testid="stat-devices-offline" sub="Never shown as healthy" />
            <Stat label="Critical incidents" value={d.incidents.critical} icon={AlertOctagon} tone={d.incidents.critical ? "bad" : null} testid="stat-critical" />
            <Stat label="Open incidents" value={d.incidents.open} icon={AlertOctagon} tone={d.incidents.open ? "warn" : null} testid="stat-open-incidents" />
            <Stat label="Resolved incidents" value={d.incidents.resolved} icon={CheckCircle2} tone="good" testid="stat-resolved" />
            <Stat label="Verified auto-repairs" value={d.automation.verified_repairs} icon={Wrench} tone="good" testid="stat-repairs" sub={`${d.automation.failed} failed / escalated`} />
            <Stat label="Awaiting approval" value={d.automation.pending_approvals} icon={Clock} tone={d.automation.pending_approvals ? "warn" : null} testid="stat-approvals" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <Stat label="Low disk space" value={d.devices.low_disk} icon={HardDrive} tone={d.devices.low_disk ? "warn" : null} testid="stat-low-disk" />
            <Stat label="High CPU" value={d.devices.high_cpu} icon={Cpu} testid="stat-high-cpu" />
            <Stat label="High memory" value={d.devices.high_mem} icon={MemoryStick} testid="stat-high-mem" />
            <Stat label="Backup warnings" value={d.incidents.backup_warnings} icon={DatabaseBackup} tone={d.incidents.backup_warnings ? "warn" : null} testid="stat-backup" />
          </div>
        </>
      )}
      {d.devices.total > 0 && <RepairFeed orgId={orgId} />}
      <div className="grid lg:grid-cols-4 gap-6 mt-8">
        <RealDeviceCard v={d.real_validation} />
        <div className="card-flat p-6" data-testid="subscription-usage-card">
          <h3 className="font-bold">Subscription usage</h3>
          <div className="mt-3 text-sm text-gray-600 space-y-1">
            <div>Plan: <b className="text-gray-900">{sub.plan_id}</b> · {sub.computed_status}</div>
            {sub.trial_ends_at && sub.plan_id === "trial" && <div>Trial ends {fmtTime(sub.trial_ends_at)}</div>}
            <div>Devices: {sub.devices_used} / {sub.device_limit}</div>
            <div className="h-1.5 bg-gray-100 mt-1"><div className="h-full bg-red-600" style={{ width: `${Math.min(100, (sub.devices_used / Math.max(1, sub.device_limit)) * 100)}%` }} /></div>
            <div>AI requests this month: {sub.ai_used_this_month} / {sub.ai_monthly_limit}</div>
          </div>
        </div>
        <div className="card-flat p-6" data-testid="maintenance-card">
          <h3 className="font-bold">Upcoming maintenance</h3>
          <p className="mt-3 text-sm text-gray-600">Window: days {(d.maintenance_window?.days || []).join(", ") || "any"} (0=Sun), {d.maintenance_window?.start_hour}:00–{d.maintenance_window?.end_hour}:00 UTC. L3 actions may auto-run only inside this window when allowed by policy.</p>
        </div>
        <div className="card-flat p-6" data-testid="recent-activity-card">
          <h3 className="font-bold">Recent activity</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {d.recent_activity.length === 0 && <li className="text-gray-500">No activity yet.</li>}
            {d.recent_activity.map((a, i) => <li key={i} className="flex justify-between gap-2"><span className="font-mono text-xs">{a.action}</span><span className="text-gray-400 text-xs whitespace-nowrap">{ago(a.created_at)}</span></li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}
